<template>
  <div
    class="hero-landscape"
    :class="{ 'is-still': heroState.reducedMotion, 'is-settled': settled, 'hl-legacy': legacy }"
    aria-hidden="true"
  >
    <!-- Measures --hero-ground-h in px, so the meadow line matches the contract. -->
    <div ref="probe" class="hl-probe"></div>

    <!-- One parallax wrapper per depth (overscanned so no edge ever shows),
         and inside it a wrapper that rises into place on entrance. The SVG
         content is generated for the real size (see HeroLandscapeScene.js).
         Static scenery and living trees are separate SVG layers: only the
         tree layers get their own compositing layer, so growing and swaying
         trees never make the browser repaint the scenery. The scenery's
         colours transition with the theme in CSS; the tree layers take the
         theme from their tone class and crossfade (see crossfade).
         Back to front: the far mountains, the mist in front of them, the
         two distant rows of the forest (with the back row's ground and
         mist between them), the middle row's ground and mist, the ridge
         row, its ground and mist, the front row with its ground, and the
         meadow. -->
    <div class="hl-par hl-par-far">
      <div class="hl-rise hl-rise-far">
        <svg ref="far" class="hl-svg hl-svg--scenery hl-scenery-far" preserveAspectRatio="xMidYMax slice" focusable="false"></svg>
      </div>
    </div>
    <div class="hl-par hl-par-mid">
      <div class="hl-rise hl-rise-mid">
        <svg ref="midBack" class="hl-svg hl-svg--scenery hl-scenery-mid-back" preserveAspectRatio="xMidYMax slice" focusable="false"></svg>
        <svg ref="midTrees" class="hl-svg hl-svg--live" :class="toneClass" preserveAspectRatio="xMidYMax slice" focusable="false"></svg>
        <svg ref="midFront" class="hl-svg hl-svg--scenery hl-scenery-mid-front" preserveAspectRatio="xMidYMax slice" focusable="false"></svg>
      </div>
    </div>
    <div class="hl-par hl-par-near">
      <div class="hl-rise hl-rise-near">
        <svg ref="rowA" class="hl-svg hl-svg--live" :class="toneClass" preserveAspectRatio="xMidYMax slice" focusable="false"></svg>
        <svg ref="nearMid" class="hl-svg hl-svg--scenery hl-scenery-near-mid" preserveAspectRatio="xMidYMax slice" focusable="false"></svg>
        <svg ref="rowB" class="hl-svg hl-svg--live" :class="toneClass" preserveAspectRatio="xMidYMax slice" focusable="false"></svg>
      </div>
      <div class="hl-rise hl-rise-meadow">
        <svg ref="meadow" class="hl-svg hl-svg--scenery hl-scenery-meadow" preserveAspectRatio="xMidYMax slice" focusable="false"></svg>
      </div>

      <!-- A few fireflies by night, a barely visible drift of pollen by
           day. One animated element per firefly or mote (its wander and
           its glow are two animations on the same composited layer). -->
      <div class="hl-critters">
        <span v-for="f in fireflies" :key="'ff' + f.id" class="hl-ff" :style="f.style">
          <span class="hl-ff-dot" :class="f.cls" :style="f.dotStyle"></span>
        </span>
        <span v-for="p in pollen" :key="'pl' + p.id" class="hl-pollen" :style="p.style">
          <span class="hl-pl-dot" :class="p.cls" :style="p.dotStyle"></span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { createLoop, seededRandom, clamp, exposeDebug, removeDebug } from './heroMotion';
import { gustField, breezeAt } from './HeroLandscapeWind';
import {
  createTerrain, farMarkup, mistMarkup, groundMarkup, liveGroundMarkup, meadowMarkup, forestDefs,
  rowMarkup, makeForest, planGrowth, growthAt, growScale, ROWS, WAVE_END
} from './HeroLandscapeScene';

// Every SVG layer, back to front (they all share the same viewBox).
const LAYERS = ['far', 'midBack', 'midTrees', 'midFront', 'rowA', 'nearMid', 'rowB', 'meadow'];
// The layers of living trees, which crossfade at dusk and dawn.
const LIVE_LAYERS = ['midTrees', 'rowA', 'rowB'];
// After the entrance, slow sway and growth need no more than ~30 redraws a
// second (each step stays far below a pixel), which halves the repaint work.
const STEADY_FRAME = 1 / 31;
// px of horizontal overscan on each side of every parallax layer (.hl-par).
const OVERSCAN = 24;
// s of clock after which the entrance rise is over (see .is-settled).
const SETTLED_AT = 2.1;
// Units beyond the visible half width within which a tree can still show.
const TREE_MARGIN = 8;
// Sway of the two nearest rows, in degrees: the lean of a passing gust at
// its peak, and the most any tree ever leans.
const GUST_DEG = 0.34;
const SWAY_MAX = 0.6;
const TAU = Math.PI * 2;
const DEG = Math.PI / 180;
// Dusk and dawn of the tree layers, in step with the sky (HeroSky.vue) and
// the scenery's token transitions below: the land follows the sky by a beat
// at dusk.
const FADE_MS = 1400;
const FADE_EASE = 'cubic-bezier(0.45, 0.05, 0.55, 0.95)';
const FADE_DELAY = { night: 200, day: 100 };
// Snapshots kept per layer if the theme is toggled again and again.
const MAX_GHOSTS = 3;

function f2(v) {
  return String(Math.round(v * 100) / 100);
}

function f4(v) {
  return String(Math.round(v * 10000) / 10000);
}

// Writes an SVG attribute only when its value changed: most frames only the
// sway of a few trees changes, everything else stays put.
function setAttr(el, name, value) {
  const key = '_hl_' + name;
  if (el[key] !== value) {
    el[key] = value;
    el.setAttribute(name, value);
  }
}

// The site's theme: night unless the page is explicitly light.
function readTone() {
  if (typeof document === 'undefined') return 'night';
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'day' : 'night';
}

function canAnimate() {
  return typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
}

// How many trees of the later growth grow at once: one on phones, up to
// three on desktops (the lanes of planGrowth; width is the hero's).
function lanesFor(width) {
  return width < 640 ? 1 : (width < 960 ? 2 : 3);
}

export default {
  name: 'HeroLandscape',
  inject: ['heroState'],
  data() {
    return {
      fireflies: [],
      pollen: [],
      settled: false,
      // Without registered custom properties (@property) the colour tokens
      // can't transition, so the scenery's shapes transition their own
      // fills instead (set in mounted).
      legacy: false,
      // The theme the tree layers are drawn in (see crossfade).
      tone: readTone()
    };
  },
  computed: {
    toneClass() {
      return 'hl-tone-' + this.tone;
    }
  },
  watch: {
    'heroState.width': 'queueMeasure',
    'heroState.height': 'queueMeasure',
    'heroState.running': 'syncLoop',
    'heroState.reducedMotion'(still) {
      if (!still) {
        // Motion switched back on: the forest carries on as it was shown,
        // fully grown, rather than replaying the entrance from bare ground
        // or opening gaps to fill. Only the forest's own time moves on: the
        // wind keeps the clock it shares with the grass fringe
        // (HeroLandscapeWind.js).
        if (this.clock + this.forestShift < WAVE_END) {
          this.forestShift = WAVE_END + 1 - this.clock;
          this.settled = true;
        }
        this.completeForest();
      }
      this.syncLoop();
      this.render(this.clock);
    }
  },
  created() {
    // Plain, non-reactive state: the simulation clock (seconds, the sum of
    // the frame deltas, so a pause never makes anything jump) and the scene.
    this.clock = 0;
    // s the forest runs ahead of the clock (see the reducedMotion watcher).
    this.forestShift = 0;
    this.lastRender = 0;
    this.built = null;
    this.half = 0;
    this.box = '';
    this.narrow = false;
    this.unitPx = 2;
    this.trees = [];
    this.toned = [];
    this.plan = null;
    this.lanes = 1;
    this.ghosts = [];
    this.debugPaused = false;
    const prefix = 'hl' + this._uid + '-';
    const pair = (name) => ({ day: prefix + name + '-day', night: prefix + name + '-night' });
    this.ids = {
      hazeDay: prefix + 'haze-day',
      hazeNight: prefix + 'haze-night',
      duskFar: prefix + 'dusk-far',
      duskMid: prefix + 'dusk-mid',
      mistDay: prefix + 'mist-day',
      mistNight: prefix + 'mist-night',
      meadowSheen: prefix + 'meadow-sheen',
      meadowSheenNight: prefix + 'meadow-sheen-night',
      // Per forest row: its trees' gradient and its mist, in both tones.
      tree: ROWS.map((row, i) => pair('tree' + i)),
      haze: ROWS.map((row, i) => pair('row-haze' + i))
    };
  },
  mounted() {
    // CSS.registerProperty alone doesn't prove @property works (Chromium
    // 78-84 has it but ignores @property rules). A registered <color> token
    // computes to rgb(); an unregistered one keeps its raw value.
    const probe = window.getComputedStyle(this.$el).getPropertyValue('--hl-far-back').trim();
    this.legacy = !/^rgba?\(/i.test(probe);

    this.loop = createLoop((dt) => {
      this.clock += dt / 1000;
      if (!this.settled && this.clock > SETTLED_AT) this.settled = true;
      if (this.clock + this.forestShift < WAVE_END + 0.6 || this.clock - this.lastRender >= STEADY_FRAME) {
        this.render(this.clock);
      }
    });
    this.measure();
    this.syncLoop();
    if (typeof MutationObserver !== 'undefined') {
      this.themeObserver = new MutationObserver(() => this.onTheme());
      this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    }
    exposeDebug('landscape', {
      pause: () => {
        this.debugPaused = true;
        this.syncLoop();
      },
      resume: () => {
        this.debugPaused = false;
        this.syncLoop();
      },
      renderAt: (ms) => {
        this.clock = ms / 1000;
        this.forestShift = 0;
        this.settled = this.clock > SETTLED_AT;
        this.render(this.clock);
      },
      time: () => Math.round(this.clock * 1000),
      stats: () => this.stats()
    });
  },
  beforeDestroy() {
    this.destroyed = true;
    this.loop.stop();
    if (this.themeObserver) this.themeObserver.disconnect();
    this.clearGhosts();
    removeDebug('landscape');
  },
  methods: {
    queueMeasure() {
      if (this.measureQueued) return;
      this.measureQueued = true;
      this.$nextTick(() => {
        this.measureQueued = false;
        if (!this.destroyed) this.measure();
      });
    },
    syncLoop() {
      if (this.heroState.running && !this.heroState.reducedMotion && !this.debugPaused) {
        this.loop.start();
      } else {
        this.loop.stop();
      }
    },
    // Fits the scene to the band's real size. The geometry is built with a
    // margin, so ordinary resizes only change the viewBox; it is rebuilt
    // (instantly, the forest's state being a function of time) only when
    // the width or the meadow line changes meaningfully.
    measure() {
      const root = this.$el;
      const width = root.clientWidth;
      const height = root.clientHeight;
      if (!width || !height) return;
      this.unitPx = height / 100;
      // On small screens the ridge row's breeze sway would move its tips
      // well under a pixel: it only leans when a gust passes (see render).
      this.narrow = width < 768;
      const ground = this.$refs.probe.offsetHeight || height * 0.18;
      const half = ((width + OVERSCAN * 2) / height) * 50;
      const meadowLine = Math.round(1000 * (1 - ground / height)) / 10;
      const b = this.built;
      if (!b || half > b.range - 2 || half < b.half * 0.8 || Math.abs(meadowLine - b.meadowLine) > 0.6) {
        this.build(half, meadowLine, width);
      }
      this.half = half;
      const box = `${f2(-half)} 0 ${f2(half * 2)} 100`;
      if (box !== this.box) {
        // A dusk snapshot would no longer line up with the new view.
        this.clearGhosts();
        this.box = box;
        LAYERS.forEach((name) => this.$refs[name].setAttribute('viewBox', box));
      }
      if (!this.critterWidth || Math.abs(width - this.critterWidth) / this.critterWidth > 0.15) {
        this.buildCritters(width);
      }
      this.render(this.clock);
    },
    build(half, meadowLine, width) {
      this.clearGhosts();
      const range = half * 1.15 + 14;
      const T = createTerrain(meadowLine);
      const refs = this.$refs;
      const ids = this.ids;
      const tone = this.tone;
      const forest = makeForest(T, range, half);
      refs.far.innerHTML = farMarkup(T, -range, range, ids);
      refs.midBack.innerHTML = mistMarkup(T, -range, range, ids);
      refs.midTrees.innerHTML = forestDefs(ids, [0, 1], [0]) +
        rowMarkup(0, forest[0], ids, tone) +
        liveGroundMarkup(T, 0, -range, range, ids, tone) +
        rowMarkup(1, forest[1], ids, tone);
      refs.midFront.innerHTML = groundMarkup(T, 1, -range, range, ids);
      refs.rowA.innerHTML = forestDefs(ids, [2], []) + rowMarkup(2, forest[2], ids, tone);
      refs.nearMid.innerHTML = groundMarkup(T, 2, -range, range, ids);
      // The front row's ground rises with the trees it hides the feet of.
      refs.rowB.innerHTML = forestDefs(ids, [3], []) + rowMarkup(3, forest[3], ids, tone) +
        liveGroundMarkup(T, 3, -range, range, ids, tone);
      refs.meadow.innerHTML = meadowMarkup(T, -range, range, ids);

      const groups = [
        refs.midTrees.querySelector('.hl-row-0'),
        refs.midTrees.querySelector('.hl-row-1'),
        refs.rowA.querySelector('.hl-row-2'),
        refs.rowB.querySelector('.hl-row-3')
      ];
      const trees = [];
      forest.forEach((list, i) => {
        const nodes = groups[i].children;
        list.forEach((tree, j) => {
          tree.el = nodes[j];
          tree.shown = false;
          tree.p = -1;
          tree.angle = NaN;
          // The sway step that moves the tip ~0.22 px at this size: a tree
          // is only redrawn when its lean changes visibly.
          tree.step = clamp((0.22 / Math.max(tree.h * this.unitPx, 1)) / DEG, 0.05, 0.3);
          trees.push(tree);
        });
      });
      this.trees = trees;
      this.toned = [];
      LIVE_LAYERS.forEach((name) => {
        const list = refs[name].querySelectorAll('.hl-toned');
        for (let i = 0; i < list.length; i++) this.toned.push(list[i]);
      });
      // The spots the entrance leaves empty fill one tree at a time; a
      // rebuild keeps every tree that can have shown, and opens no new gap.
      this.lanes = lanesFor(width);
      this.plan = planGrowth(trees, half, this.lanes, this.clock + this.forestShift, this.plan);
      if (this.heroState.reducedMotion) this.completeForest();
      this.built = { half, range, meadowLine };
    },
    // Every empty spot counts as grown from now on (reduced motion shows the
    // full forest, which must not open gaps when motion comes back).
    completeForest() {
      const plan = {};
      this.trees.forEach((tree) => {
        if (!tree.free) return;
        tree.start = -1;
        tree.dur = 1;
        tree.planned = false;
        plan[tree.key] = { start: -1, dur: 1, planned: false };
      });
      this.plan = plan;
    },
    // Fireflies and pollen are plain CSS animations on composited HTML
    // layers (cheap); only their layout is generated here.
    buildCritters(width) {
      this.critterWidth = width;
      const r = seededRandom(5150);
      const pct = (v) => v.toFixed(2) + '%';
      const sec = (v) => v.toFixed(2) + 's';
      const px = (v) => v.toFixed(1) + 'px';
      const swing = () => (r() < 0.5 ? 'alternate' : 'alternate-reverse');

      // A few small fireflies over the forest, glowing slowly.
      const fireflies = [];
      const fireflyCount = clamp(Math.round(width / 110), 6, 14);
      for (let i = 0; i < fireflyCount; i++) {
        const size = 2 + r() * 1.2;
        fireflies.push({
          id: i,
          // At nightfall they light up one after another.
          style: { left: pct(2 + r() * 96), top: pct(30 + r() * 60), '--hl-delay': sec(0.3 + i * 0.16) },
          cls: 'is-w' + (1 + Math.floor(r() * 2.999)),
          // Wander, then glow (the order of .hl-ff-dot's animation-name).
          dotStyle: {
            width: px(size),
            height: px(size),
            left: px(-size / 2),
            top: px(-size / 2),
            animationDuration: `${sec(12 + r() * 9)}, ${sec(5 + r() * 4)}`,
            animationDelay: `${sec(-r() * 20)}, ${sec(-r() * 9)}`,
            animationDirection: `${swing()}, normal`
          }
        });
      }

      // A handful of tiny, faint motes drifting in the morning light.
      const pollen = [];
      const pollenCount = clamp(Math.round(width / 320), 2, 5);
      for (let i = 0; i < pollenCount; i++) {
        const size = 1.4 + r() * 0.7;
        pollen.push({
          id: i,
          style: { left: pct(5 + r() * 90), top: pct(36 + r() * 50), '--hl-delay': sec(0.2 + i * 0.2) },
          cls: 'is-w' + (1 + Math.floor(r() * 1.999)),
          dotStyle: {
            width: px(size),
            height: px(size),
            left: px(-size / 2),
            top: px(-size / 2),
            animationDuration: `${sec(14 + r() * 8)}, ${sec(5 + r() * 3)}`,
            animationDelay: `${sec(-r() * 18)}, ${sec(-r() * 6)}`,
            animationDirection: `${swing()}, normal`
          }
        });
      }

      this.fireflies = fireflies;
      this.pollen = pollen;
    },
    render(t) {
      if (!this.built) return;
      this.lastRender = t;
      const still = this.heroState.reducedMotion;
      // The wind's time (shared with the grass fringe) and the forest's.
      const ft = t + this.forestShift;
      const half = this.half || this.built.half;
      const reach = half + TREE_MARGIN;
      const gust = still ? null : gustField(t);
      const breeze = breezeAt(t);
      const trees = this.trees;
      for (let i = 0; i < trees.length; i++) {
        const tree = trees[i];
        // Trees in the build margin, out of view, are left as they are.
        if (tree.x > reach || tree.x < -reach) continue;
        const p = still ? 1 : growthAt(tree, ft);
        if (p <= 0) {
          if (tree.shown) {
            setAttr(tree.el, 'display', 'none');
            tree.shown = false;
          }
          continue;
        }
        if (!tree.shown) {
          setAttr(tree.el, 'display', 'inline');
          tree.shown = true;
        }
        let angle = 0;
        if (tree.sways && !still) {
          // A slow breeze of two harmonics plus the passing gusts (they
          // cross the hero from left to right), never more than SWAY_MAX.
          let a = gust.at((tree.x + half) / (half * 2)) * GUST_DEG;
          if (!(this.narrow && tree.row === 2)) {
            const w = TAU / tree.period;
            a += tree.amp * breeze * (0.72 * Math.sin(w * t + tree.phase) + 0.28 * Math.sin(w * 2.7 * t + tree.phase * 1.7));
          }
          a = clamp(a, -SWAY_MAX, SWAY_MAX);
          angle = Math.round(a / tree.step) * tree.step;
        }
        if (p === tree.p && angle === tree.angle) continue;
        tree.p = p;
        tree.angle = angle;
        this.place(tree, p, angle);
      }
    },
    // One transform per tree: its shape scaled to its height, standing on
    // its row's ground (its feet sunk below it), growing from the ground
    // line up (the lab's sy 0.04 -> 1 and sx = sy^0.85) and leaning from
    // there.
    place(tree, p, angle) {
      const sy = growScale(p);
      const sx = p >= 1 ? 1 : Math.pow(sy, 0.85);
      const sink = tree.sink * sy;
      let a = sx * tree.scale;
      let b = 0;
      let c = 0;
      let d = sy * tree.scale;
      let e = tree.x;
      let f = tree.ground + sink;
      if (angle !== 0) {
        const cos = Math.cos(angle * DEG);
        const sin = Math.sin(angle * DEG);
        a *= cos;
        b = sin * sx * tree.scale;
        c = -sin * d;
        d *= cos;
        e = tree.x - sin * sink;
        f = tree.ground + cos * sink;
      }
      setAttr(tree.el, 'transform', `matrix(${f4(a)} ${f4(b)} ${f4(c)} ${f4(d)} ${f2(e)} ${f2(f)})`);
    },
    onTheme() {
      const tone = readTone();
      if (tone === this.tone) return;
      this.crossfade(FADE_DELAY[tone]);
      // The live layers switch at once (their tone class is patched before
      // the next frame, their gradient fills right here), under the
      // snapshots taken just above.
      this.tone = tone;
      for (let i = 0; i < this.toned.length; i++) {
        const el = this.toned[i];
        el.setAttribute('fill', el.getAttribute('data-' + tone));
      }
    },
    // Dusk and dawn for the living trees. Transitioning their colours would
    // restyle thousands of shapes on every frame of the fade; instead each
    // tree layer switches to the new colours at once, under a snapshot of
    // itself in the old colours (it keeps its old tone class and fills)
    // that fades out on its own compositing layer: one restyle, then a
    // fade the compositor runs alone. The trees barely sway in 1.6 s, so
    // the frozen snapshot is indistinguishable from the live layer beneath.
    crossfade(delay) {
      if (!this.built || !canAnimate()) return;
      if (this.ghosts.length >= MAX_GHOSTS * LIVE_LAYERS.length) {
        this.ghosts.slice(0, LIVE_LAYERS.length).forEach((entry) => this.dropGhost(entry, true));
      }
      LIVE_LAYERS.forEach((name) => {
        const live = this.$refs[name];
        const ghost = live.cloneNode(true);
        // The defs are the live layer's (no duplicate ids; they hold both
        // tones' gradients); hidden shapes would only cost styling.
        const strip = ghost.querySelectorAll('defs, [display="none"]');
        for (let i = 0; i < strip.length; i++) {
          if (strip[i].parentNode) strip[i].parentNode.removeChild(strip[i]);
        }
        ghost.setAttribute('class', ghost.getAttribute('class') + ' hl-ghost');
        // Right above its live layer, below any older snapshot still fading,
        // so a quick toggle back carries on from what is on screen.
        live.parentNode.insertBefore(ghost, live.nextSibling);
        const anim = ghost.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: FADE_MS,
          delay,
          easing: FADE_EASE,
          fill: 'both'
        });
        const entry = { el: ghost, anim };
        anim.onfinish = () => this.dropGhost(entry, false);
        this.ghosts.push(entry);
      });
    },
    dropGhost(entry, cancel) {
      const i = this.ghosts.indexOf(entry);
      if (i >= 0) this.ghosts.splice(i, 1);
      entry.anim.onfinish = null;
      if (cancel) entry.anim.cancel();
      if (entry.el.parentNode) entry.el.parentNode.removeChild(entry.el);
    },
    clearGhosts() {
      if (!this.ghosts.length) return;
      this.ghosts.slice().forEach((entry) => this.dropGhost(entry, true));
    },
    stats() {
      const visible = { empty: 0, growing: 0, grown: 0 };
      const treesByRow = [0, 0, 0, 0];
      const growingByRow = [0, 0, 0, 0];
      const still = this.heroState.reducedMotion;
      const ft = this.clock + this.forestShift;
      let fullAt = 0;
      this.trees.forEach((tree) => {
        if (tree.planned) fullAt = Math.max(fullAt, tree.start + tree.dur);
        if (Math.abs(tree.x) > this.half) return;
        treesByRow[tree.row]++;
        const p = still ? 1 : growthAt(tree, ft);
        if (p <= 0) {
          visible.empty++;
        } else if (p < 1) {
          visible.growing++;
          growingByRow[tree.row]++;
        } else {
          visible.grown++;
        }
      });
      return {
        time: Math.round(this.clock * 1000),
        forestShift: Math.round(this.forestShift * 1000),
        trees: this.trees.length,
        visible,
        treesByRow,
        growingByRow,
        lanes: this.lanes,
        // s of forest time when the last empty spot in view is grown.
        fullAt: Math.round(fullAt * 10) / 10,
        ghosts: this.ghosts.length,
        legacy: this.legacy,
        nodes: this.$el.querySelectorAll('*').length,
        built: this.built
      };
    }
  }
}
</script>

<style scoped lang="scss">
// The theme's colour tokens of the static scenery. They are registered
// (@property) as colours so that they can transition at dusk and dawn.
$hl-night: (
  far-back: #21403c,
  far-back-sh: #193430,
  far-front: #1a3733,
  far-front-sh: #142d2a,
  rim-far: rgba(176, 218, 208, 0.26),
  rim-front: rgba(160, 206, 194, 0.2),
  g1: #173d29,
  g2: #11331f,
  md: #173a1f,
  bl-d: #1f4a28,
  bl-l: #2c6034
);

// Day: a soft, hazy morning.
$hl-day: (
  far-back: #bed8d1,
  far-back-sh: #a9cac2,
  far-front: #a6c8be,
  far-front-sh: #92b9ae,
  rim-far: rgba(255, 255, 255, 0.7),
  rim-front: rgba(255, 255, 250, 0.55),
  g1: #3c8a4c,
  g2: #27733b,
  md: #8cc46a,
  bl-d: #6aa64f,
  bl-l: #9fd27a
);

// The tokens each static scenery layer uses: only these transition, on the
// layer's own SVG, so a theme change restyles a few shapes per frame.
$hl-scenery: (
  far: (far-back, far-back-sh, far-front, far-front-sh, rim-far, rim-front),
  mid-front: (g1,),
  near-mid: (g2,),
  meadow: (md, bl-d, bl-l)
);

// The forest's flat colours (the lab's palette; its gradients are in
// HeroLandscapeScene.js): the grounds drawn in the tree layers, and the lit
// sliver of the two nearest rows (the lab's trunks always stand below the
// ground, see spire). The tree layers take them from their tone class, so a
// dusk snapshot keeps its colours.
$hl-forest: (
  night: (
    g0: #1d4436,
    g3: #143520,
    lit2: #8fb5a6,
    lit2-o: 0.1,
    lit3: #9cc2b2,
    lit3-o: 0.13
  ),
  day: (
    g0: #5f9f70,
    g3: #5fa04e,
    lit2: #7cc46c,
    lit2-o: 0.6,
    lit3: #82c96e,
    lit3-o: 0.65
  )
);

// Same timing as the sky (HeroSky.vue), so sky and land change as one
// illustration; the land follows the sky by a beat at dusk.
$hl-ease: cubic-bezier(0.45, 0.05, 0.55, 0.95);
$hl-theme-t: 1.4s;
// A lazy drift for the critters: it slows at each waypoint without stopping.
$hl-drift: cubic-bezier(0.3, 0.1, 0.7, 0.9);

@function hl-token-transitions($names, $duration, $timing, $delay) {
  $list: ();
  @each $name in $names {
    $list: append($list, unquote('--hl-#{$name}') $duration $timing $delay, comma);
  }
  @return $list;
}

@each $name, $value in $hl-night {
  @property --hl-#{$name} {
    syntax: '<color>';
    inherits: true;
    initial-value: $value;
  }
}

.hero-landscape {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: var(--hero-land-h);
  pointer-events: none;

  // Night (the site's default theme). Distant layers are hazier and bluer,
  // the near forest darker, the moon lights the right-hand sides.
  @each $name, $value in $hl-night {
    --hl-#{$name}: #{$value};
  }
}

[data-theme="light"] .hero-landscape {
  @each $name, $value in $hl-day {
    --hl-#{$name}: #{$value};
  }
}

@each $layer, $names in $hl-scenery {
  .hl-scenery-#{$layer} {
    transition: hl-token-transitions($names, $hl-theme-t, $hl-ease, 0.2s);
  }

  [data-theme="light"] .hl-scenery-#{$layer} {
    transition: hl-token-transitions($names, $hl-theme-t, $hl-ease, 0.1s);
  }
}

@each $tone, $c in $hl-forest {
  .hl-tone-#{$tone} ::v-deep {
    .hl-g0 { fill: map-get($c, g0); }
    .hl-g3 { fill: map-get($c, g3); }

    .hl-row-2 .hl-lt {
      fill: map-get($c, lit2);
      fill-opacity: map-get($c, lit2-o);
    }

    .hl-row-3 .hl-lt {
      fill: map-get($c, lit3);
      fill-opacity: map-get($c, lit3-o);
    }
  }
}

.hl-probe {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 1px;
  height: var(--hero-ground-h);
  visibility: hidden;
}

/* ---------------------------------------------------------------------- */
/* Layers: parallax, entrance                                             */
/* ---------------------------------------------------------------------- */

// The 3D translation already gives each depth its own compositing layer
// (no will-change: a dozen full-width hints overrun Firefox's budget).
.hl-par {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -24px;
  right: -24px;
}

.hl-par-far {
  transform: translate3d(calc(var(--hero-px, 0) * 5px), 0, 0);
}

.hl-par-mid {
  transform: translate3d(calc(var(--hero-px, 0) * 9px), 0, 0);
}

.hl-par-near {
  transform: translate3d(calc(var(--hero-px, 0) * 14px), 0, 0);
}

// The layers rise from below into place one after another, like a pop-up
// book opening. Once settled the animation is dropped, so the wrappers stop
// holding compositing layers of their own.
.hl-rise {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  animation: hl-rise 1.15s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.hl-rise-far { animation-delay: 0.15s; }
.hl-rise-mid { animation-delay: 0.35s; }
.hl-rise-near { animation-delay: 0.55s; }
.hl-rise-meadow { animation-delay: 0.7s; }

.is-settled .hl-rise {
  animation: none;
}

@keyframes hl-rise {
  0% {
    transform: translate3d(0, 46%, 0);
    opacity: 0;
  }
  35% {
    opacity: 1;
  }
  100% {
    transform: translate3d(0, 0, 0);
    opacity: 1;
  }
}

.hl-svg {
  position: absolute;
  left: 0;
  top: 0;
  display: block;
  width: 100%;
  height: 100%;
}

// The tree layers redraw while trees grow and sway: each gets its own
// compositing layer, so their repaints never touch the scenery.
.hl-svg--live {
  will-change: transform;
}

// A dusk / dawn snapshot of a tree layer, fading out (see crossfade).
.hl-svg.hl-ghost {
  will-change: opacity;
}

/* ---------------------------------------------------------------------- */
/* Generated SVG content                                                  */
/* ---------------------------------------------------------------------- */

.hero-landscape ::v-deep {
  // Light grazing the lit side of the far summits.
  .hl-rim {
    fill: none;
    stroke-width: 0.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .hl-rim-far { stroke: var(--hl-rim-far); }
  .hl-rim-front { stroke: var(--hl-rim-front); stroke-width: 0.42; }

  .hl-far-back { fill: var(--hl-far-back); }
  .hl-far-back-sh { fill: var(--hl-far-back-sh); }
  .hl-far-front { fill: var(--hl-far-front); }
  .hl-far-front-sh { fill: var(--hl-far-front-sh); }
  .hl-g1 { fill: var(--hl-g1); }
  .hl-g2 { fill: var(--hl-g2); }
  .hl-md { fill: var(--hl-md); }
  .hl-bl-d { fill: var(--hl-bl-d); }
  .hl-bl-l { fill: var(--hl-bl-l); }
  .hl-fl-w { fill: #fff8e1; }
  .hl-fl-y { fill: #ffd36e; }
  .hl-fl-p { fill: #f6a6b8; }
  .hl-fl-c { fill: #efb441; }

  // Day haze / night mist overlays of the scenery (and the day-only
  // flowers) crossfade with the sky.
  .hl-svg .hl-day,
  .hl-svg .hl-night {
    transition: opacity $hl-theme-t $hl-ease 0.2s;
  }

  .hl-svg .hl-day { opacity: 0; }
  .hl-svg .hl-night { opacity: 1; }

  // Sunset / sunrise light on the mountains and the mist, in step with the
  // sky's dusk (HeroSky.vue .sky-dusk): steady states dark = 0 x 1 and
  // light = 1 x 0, so it only glows while the theme changes — the factor
  // that rises does so quickly and the one that falls lingers.
  .hl-svg .hl-dusk {
    opacity: 0;
    transition: opacity 1.6s $hl-ease 0.4s;
  }

  .hl-svg .hl-dusk-in {
    opacity: 1;
    transition: opacity 0.5s ease-out;
  }
}

[data-theme="light"] .hero-landscape ::v-deep {
  .hl-svg .hl-day {
    opacity: 1;
    transition-delay: 0.1s;
  }

  .hl-svg .hl-night {
    opacity: 0;
    transition-delay: 0.1s;
  }

  .hl-svg .hl-dusk {
    opacity: 1;
    transition: opacity 0.45s ease-out;
  }

  .hl-svg .hl-dusk-in {
    opacity: 0;
    transition: opacity 1.7s $hl-ease 0.45s;
  }
}

// Fallback for browsers without @property: the scenery's shapes transition
// their own colours at dusk and dawn.
.hl-legacy ::v-deep {
  .hl-svg--scenery path,
  .hl-svg--scenery rect {
    transition: fill $hl-theme-t $hl-ease 0.15s, stroke $hl-theme-t $hl-ease 0.15s;
  }
}

/* ---------------------------------------------------------------------- */
/* Fireflies (night) and pollen (day)                                     */
/* ---------------------------------------------------------------------- */

.hl-critters {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  animation: hl-fade-in 1.6s ease 2.8s both;
}

@keyframes hl-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.hl-ff,
.hl-pollen {
  position: absolute;
  width: 0;
  height: 0;
}

// At nightfall the fireflies light up one after another; at sunrise they
// all go out together, then leave raster and compositing (visibility).
.hl-ff {
  opacity: 1;
  visibility: visible;
  transition: opacity 0.9s ease calc(0.6s + var(--hl-delay, 0s)), visibility 0s;
}

[data-theme="light"] .hl-ff {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.5s ease 0s, visibility 0s linear 0.5s;
}

.hl-pollen {
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.5s ease 0s, visibility 0s linear 0.5s;
}

[data-theme="light"] .hl-pollen {
  opacity: 1;
  visibility: visible;
  transition: opacity 1s ease calc(0.5s + var(--hl-delay, 0s)), visibility 0s;
}

// Critters of the other half of the day rest (paused, so nothing invisible
// keeps animating; they resume exactly where they stopped). Specific enough
// to beat the animation shorthands below.
[data-theme="light"] .hl-critters .hl-ff *,
.hl-critters .hl-pollen * {
  animation-play-state: paused;
}

[data-theme="light"] .hl-critters .hl-pollen * {
  animation-play-state: running;
}

// A firefly: a lazy wander through a few waypoints (back and forth) and a
// slow, soft glow, on one element.
.hl-ff-dot {
  display: block;
  position: absolute;
  left: -1px;
  top: -1px;
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background: #f4ffb0;
  box-shadow: 0 0 4px 1px rgba(220, 255, 140, 0.45), 0 0 10px 3px rgba(220, 255, 140, 0.14);
  animation-name: hl-ff-w1, hl-ff-glow;
  animation-duration: 15s, 6s;
  animation-timing-function: $hl-drift, ease-in-out;
  animation-iteration-count: infinite;
  animation-direction: alternate, normal;
}

.hl-ff-dot.is-w2 { animation-name: hl-ff-w2, hl-ff-glow; }
.hl-ff-dot.is-w3 { animation-name: hl-ff-w3, hl-ff-glow; }

@keyframes hl-ff-w1 {
  0% { transform: translate3d(-24px, 8px, 0); }
  28% { transform: translate3d(-8px, -10px, 0); }
  52% { transform: translate3d(10px, -3px, 0); }
  76% { transform: translate3d(22px, -13px, 0); }
  100% { transform: translate3d(8px, 10px, 0); }
}

@keyframes hl-ff-w2 {
  0% { transform: translate3d(18px, -6px, 0); }
  30% { transform: translate3d(2px, 12px, 0); }
  55% { transform: translate3d(-16px, 3px, 0); }
  78% { transform: translate3d(-30px, -9px, 0); }
  100% { transform: translate3d(-10px, -15px, 0); }
}

@keyframes hl-ff-w3 {
  0% { transform: translate3d(-8px, -13px, 0); }
  33% { transform: translate3d(15px, -5px, 0); }
  66% { transform: translate3d(6px, 14px, 0); }
  100% { transform: translate3d(-20px, 5px, 0); }
}

// Mostly dim, then a slow, soft glow.
@keyframes hl-ff-glow {
  0%, 30% { opacity: 0.08; }
  55% { opacity: 0.85; }
  70% { opacity: 0.7; }
  100% { opacity: 0.08; }
}

// A pollen mote: a slow, rising drift and a faint twinkle, on one element.
.hl-pl-dot {
  display: block;
  position: absolute;
  left: -1px;
  top: -1px;
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background: rgba(255, 253, 240, 0.85);
  box-shadow: 0 0 3px rgba(255, 246, 210, 0.45);
  animation-name: hl-pl-w1, hl-pl-twinkle;
  animation-duration: 16s, 6s;
  animation-timing-function: $hl-drift, ease-in-out;
  animation-iteration-count: infinite;
  animation-direction: alternate, normal;
}

.hl-pl-dot.is-w2 { animation-name: hl-pl-w2, hl-pl-twinkle; }

@keyframes hl-pl-w1 {
  0% { transform: translate3d(-28px, 8px, 0); }
  35% { transform: translate3d(-6px, -10px, 0); }
  70% { transform: translate3d(14px, -4px, 0); }
  100% { transform: translate3d(30px, -22px, 0); }
}

@keyframes hl-pl-w2 {
  0% { transform: translate3d(26px, 6px, 0); }
  40% { transform: translate3d(6px, -14px, 0); }
  70% { transform: translate3d(-12px, -6px, 0); }
  100% { transform: translate3d(-30px, -20px, 0); }
}

@keyframes hl-pl-twinkle {
  0%, 100% { opacity: 0.06; }
  50% { opacity: 0.38; }
}

/* ---------------------------------------------------------------------- */
/* Reduced motion: the composed, fully grown scene, standing still        */
/* ---------------------------------------------------------------------- */

.is-still {
  .hl-rise,
  .hl-critters,
  .hl-ff-dot {
    animation: none;
  }

  .hl-ff-dot {
    opacity: 0.5;
  }

  .hl-pollen {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hl-rise,
  .hl-critters,
  .hl-ff-dot {
    animation: none;
  }

  .hl-ff-dot {
    opacity: 0.5;
  }

  .hl-pollen {
    display: none;
  }
}
</style>
